import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';

const Navbar = () => {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [analyticsDropdownOpen, setAnalyticsDropdownOpen] = useState(false);
  const [growthDropdownOpen, setGrowthDropdownOpen] = useState(false);
  const [growthDropdownOpenPublic, setGrowthDropdownOpenPublic] = useState(false);
  const [guidesDropdownOpen, setGuidesDropdownOpen] = useState(false);
  const [guidesDropdownOpenPublic, setGuidesDropdownOpenPublic] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [mobileMenuOpen]);
  const dropdownRef = useRef(null);
  const analyticsDropdownRef = useRef(null);
  const growthDropdownRef = useRef(null);
  const growthDropdownRefPublic = useRef(null);
  const guidesDropdownRef = useRef(null);
  const guidesDropdownRefPublic = useRef(null);
  const mobileMenuRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close anything if clicking on dropdown toggles or their menus
      const isDropdownToggle = event.target.closest('.nav-dropdown-toggle') || 
                               event.target.closest('.dropdown-toggle') ||
                               event.target.closest('.nav-dropdown-menu') ||
                               event.target.closest('.dropdown-menu');
      
      if (isDropdownToggle) {
        return; // Let the dropdown toggle handle its own click
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (analyticsDropdownRef.current && !analyticsDropdownRef.current.contains(event.target)) {
        setAnalyticsDropdownOpen(false);
      }
      if (growthDropdownRef.current && !growthDropdownRef.current.contains(event.target)) {
        setGrowthDropdownOpen(false);
      }
      if (growthDropdownRefPublic.current && !growthDropdownRefPublic.current.contains(event.target)) {
        setGrowthDropdownOpenPublic(false);
      }
      if (guidesDropdownRef.current && !guidesDropdownRef.current.contains(event.target)) {
        setGuidesDropdownOpen(false);
      }
      if (guidesDropdownRefPublic.current && !guidesDropdownRefPublic.current.contains(event.target)) {
        setGuidesDropdownOpenPublic(false);
      }
      // Only close mobile menu if clicking outside the menu and not on the toggle button
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && 
          !event.target.closest('.mobile-menu-toggle') &&
          !event.target.closest('.mobile-menu-overlay')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdowns when route changes
  useEffect(() => {
    setAnalyticsDropdownOpen(false);
    setGrowthDropdownOpen(false);
    setGrowthDropdownOpenPublic(false);
    setGuidesDropdownOpen(false);
    setGuidesDropdownOpenPublic(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isAnalyticsRoute = location.pathname === '/analytics/overview' || 
                          location.pathname === '/analytics/content' || 
                          location.pathname === '/analytics/revenue' ||
                          location.pathname === '/analytics/alerts' ||
                          location.pathname === '/dashboard' ||
                          location.pathname === '/content' ||
                          location.pathname === '/revenue';

  const isGrowthRoute = location.pathname === '/growth' || 
                        location.pathname === '/growth/adsense' ||
                        location.pathname.startsWith('/growth/');

  const isGuidesRoute = location.pathname === '/guides' || 
                        location.pathname.startsWith('/guides/');

  return (
    <>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      <nav className="navbar">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <img src="/images/the-ad-shop-logo.svg" alt="The Ad Shop" className="logo" />
          </Link>
          
          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`} ref={mobileMenuRef}>
          {token ? (
            <>
              {/* Analytics Dropdown */}
              <div className="nav-dropdown" ref={analyticsDropdownRef}>
                <button 
                  className={`nav-dropdown-toggle ${isAnalyticsRoute ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnalyticsDropdownOpen(!analyticsDropdownOpen);
                    setGrowthDropdownOpen(false);
                  }}
                >
                  Analytics {analyticsDropdownOpen ? '▲' : '▼'}
                </button>
                {analyticsDropdownOpen && (
                  <div className="nav-dropdown-menu">
                    <Link 
                      to="/analytics/overview" 
                      className="nav-dropdown-item"
                      onClick={() => {
                        setAnalyticsDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      Overview
                    </Link>
                    <Link 
                      to="/analytics/content" 
                      className="nav-dropdown-item"
                      onClick={() => {
                        setAnalyticsDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      Content
                    </Link>
                    <Link 
                      to="/analytics/revenue" 
                      className="nav-dropdown-item"
                      onClick={() => {
                        setAnalyticsDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      Revenue
                    </Link>
                    <Link 
                      to="/analytics/alerts" 
                      className="nav-dropdown-item"
                      onClick={() => {
                        setAnalyticsDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      Alerts
                    </Link>
                    <Link 
                      to="/metrics-dictionary" 
                      className="nav-dropdown-item"
                      onClick={() => {
                        setAnalyticsDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      Metrics Dictionary
                    </Link>
                  </div>
                )}
              </div>

              {/* Growth Dropdown */}
              <div className="nav-dropdown" ref={growthDropdownRef}>
                <button 
                  className={`nav-dropdown-toggle ${isGrowthRoute ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setGrowthDropdownOpen(!growthDropdownOpen);
                    setAnalyticsDropdownOpen(false);
                  }}
                >
                  Growth {growthDropdownOpen ? '▲' : '▼'}
                </button>
                {growthDropdownOpen && (
                  <div className="nav-dropdown-menu">
                    <Link 
                      to="/growth/adsense" 
                      className="nav-dropdown-item"
                      onClick={() => {
                        setGrowthDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      AdSense
                    </Link>
                  </div>
                )}
              </div>

              {/* Guides Dropdown */}
              <div className="nav-dropdown" ref={guidesDropdownRef}>
                <button 
                  className={`nav-dropdown-toggle ${isGuidesRoute ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setGuidesDropdownOpen(!guidesDropdownOpen);
                    setAnalyticsDropdownOpen(false);
                    setGrowthDropdownOpen(false);
                  }}
                >
                  Guides {guidesDropdownOpen ? '▲' : '▼'}
                </button>
                {guidesDropdownOpen && (
                  <div className="nav-dropdown-menu">
                    <Link 
                      to="/guides" 
                      className="nav-dropdown-item"
                      onClick={() => {
                        setGuidesDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      All Guides
                    </Link>
                    <Link 
                      to="/guides/adsense" 
                      className="nav-dropdown-item"
                      onClick={() => {
                        setGuidesDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      AdSense
                    </Link>
                  </div>
                )}
              </div>

              {/* Sites */}
              <Link 
                to="/sites" 
                className={`nav-link ${location.pathname === '/sites' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Sites
              </Link>

              <Link to="/contact" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Support</Link>
              
              {/* User Dropdown */}
              <div className="dropdown" ref={dropdownRef}>
                <button 
                  className="dropdown-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(!dropdownOpen);
                  }}
                >
                  {user?.firstName || 'User'} ▼
                </button>
                {dropdownOpen && (
                  <div className="dropdown-menu">
                    <Link to="/profile" className="dropdown-item" onClick={() => {
                      setDropdownOpen(false);
                      setMobileMenuOpen(false);
                    }}>
                      Edit Profile
                    </Link>
                    <Link
                      to="/billing"
                      className="dropdown-item"
                      onClick={() => {
                        setDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      Subscription
                    </Link>
                    {user?.isAdmin && (
                      <Link to="/admin/dashboard" className="dropdown-item" onClick={() => {
                        setDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}>
                        Admin Dashboard
                      </Link>
                    )}
                    <button onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }} className="dropdown-item logout">
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/analytics-features" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Analytics</Link>
              <Link to="/revenue-features" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Revenue</Link>
              <Link to="/content-features" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Content</Link>
              {/* Growth Dropdown for Public */}
              <div className="nav-dropdown" ref={growthDropdownRefPublic}>
                <button 
                  className={`nav-dropdown-toggle ${isGrowthRoute ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setGrowthDropdownOpenPublic(!growthDropdownOpenPublic);
                  }}
                >
                  Growth {growthDropdownOpenPublic ? '▲' : '▼'}
                </button>
                {growthDropdownOpenPublic && (
                  <div className="nav-dropdown-menu">
                    <Link 
                      to="/growth/adsense" 
                      className="nav-dropdown-item"
                      onClick={() => {
                        setGrowthDropdownOpenPublic(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      AdSense
                    </Link>
                  </div>
                )}
              </div>
              {/* Guides Dropdown for Public */}
              <div className="nav-dropdown" ref={guidesDropdownRefPublic}>
                <button 
                  className={`nav-dropdown-toggle ${isGuidesRoute ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setGuidesDropdownOpenPublic(!guidesDropdownOpenPublic);
                  }}
                >
                  Guides {guidesDropdownOpenPublic ? '▲' : '▼'}
                </button>
                {guidesDropdownOpenPublic && (
                  <div className="nav-dropdown-menu">
                    <Link 
                      to="/guides" 
                      className="nav-dropdown-item"
                      onClick={() => {
                        setGuidesDropdownOpenPublic(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      All Guides
                    </Link>
                    <Link 
                      to="/guides/adsense" 
                      className="nav-dropdown-item"
                      onClick={() => {
                        setGuidesDropdownOpenPublic(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      AdSense
                    </Link>
                  </div>
                )}
              </div>
              <Link to="/pricing" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <Link to="/login" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Login</Link>
              <Link to="/signup" className="btn-signup" onClick={() => setMobileMenuOpen(false)}>Sign Up</Link>
            </>
          )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
